import { ExternalLink, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserBadge } from "@/components/UserBadge";
import { pillarLabel } from "@/lib/pillars";
import type { CoachPublic } from "@/lib/coachesQueries";

export function CoachCard({ coach }: { coach: CoachPublic }) {
  const initials = (coach.display_name ?? "A").slice(0, 2).toUpperCase();
  return (
    <Card className="flex flex-col gap-4 border-border/50 bg-card/80 p-6 backdrop-blur">
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12">
          {coach.avatar_url && <AvatarImage src={coach.avatar_url} alt={`Profile photo of ${coach.display_name ?? "AGES coach"}`} />}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-semibold">{coach.display_name ?? "Anonymous coach"}</h3>
            {coach.current_badge_tier && (
              <UserBadge tier={coach.current_badge_tier as any} size="sm" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">{pillarLabel(coach.selected_pillar)}</p>
        </div>
      </div>

      {coach.headline && <p className="text-sm font-medium">{coach.headline}</p>}
      {coach.bio && <p className="text-sm text-muted-foreground">{coach.bio}</p>}

      {coach.specialties.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {coach.specialties.slice(0, 6).map((s) => (
            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        {coach.hourly_rate_cents ? (
          <span className="text-sm text-muted-foreground">${(coach.hourly_rate_cents / 100).toFixed(0)}/hr</span>
        ) : <span />}
        {(() => {
          const v = (coach.booking_url ?? "").trim();
          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
          const isUrl = /^https:\/\//i.test(v);
          if (!isEmail && !isUrl) {
            return <Button size="sm" disabled title="No contact method provided">Unavailable</Button>;
          }
          const href = isEmail ? `mailto:${v}` : v;
          return (
            <Button asChild size="sm">
              <a href={href} target={isEmail ? undefined : "_blank"} rel={isEmail ? undefined : "noopener noreferrer"}>
                {isEmail ? <>Email <Mail className="ml-1 h-3.5 w-3.5" /></> : <>Book <ExternalLink className="ml-1 h-3.5 w-3.5" /></>}
              </a>
            </Button>
          );
        })()}
      </div>
    </Card>
  );
}
